import { NOTIFICATION_TYPES } from "@/constants/notification-types";
import { Post } from "@/types/post";
import { hasGeoPoint, haversineMeters, LatLng } from "@/utils/geo.web";

export type EventTypeKey = keyof typeof NOTIFICATION_TYPES;
export const EVENT_TYPE_KEYS = Object.keys(NOTIFICATION_TYPES) as EventTypeKey[];

export type AggregatedPost = {
  id: string;
  description: string;
  distanceMeters: number;
};

export type AggregatedCard = {
  key: EventTypeKey;
  count: number;
  nearestMeters: number;
  posts: AggregatedPost[];
};

export type AggregatedReport = {
  total: number;
  cards: AggregatedCard[];
};

const toEventTypeKey = (raw: string | undefined): EventTypeKey =>
  raw && raw in NOTIFICATION_TYPES ? (raw as EventTypeKey) : "OTHER";

/**
 * Buckets posts within `radiusMeters` of `userPos` by `eventType`. Posts
 * without a geoPoint are skipped. Each card's `posts` are sorted by distance
 * ascending; cards themselves are sorted by `count` descending. Unknown event
 * types fall into the `OTHER` bucket.
 */
export function aggregateNearbyPosts(
  posts: Post[],
  userPos: LatLng,
  opts: { radiusMeters: number },
): AggregatedReport {
  const byType = new Map<EventTypeKey, AggregatedCard>();
  let total = 0;

  for (const post of posts) {
    if (!hasGeoPoint(post)) continue;
    const meters = haversineMeters(userPos, {
      lat: post.location.geoPoint.latitude,
      lng: post.location.geoPoint.longitude,
    });
    if (meters > opts.radiusMeters) continue;

    total += 1;
    const key = toEventTypeKey(post.eventType);
    const item: AggregatedPost = {
      id: post.id,
      description: post.description ?? "",
      distanceMeters: meters,
    };
    const existing = byType.get(key);
    if (existing) {
      existing.count += 1;
      existing.nearestMeters = Math.min(existing.nearestMeters, meters);
      existing.posts.push(item);
    } else {
      byType.set(key, {
        key,
        count: 1,
        nearestMeters: meters,
        posts: [item],
      });
    }
  }

  for (const card of byType.values()) {
    card.posts.sort((a, b) => a.distanceMeters - b.distanceMeters);
  }

  const cards = EVENT_TYPE_KEYS.map((key) => byType.get(key) ?? null)
    .filter((c): c is AggregatedCard => !!c)
    .sort((a, b) => b.count - a.count);

  return { total, cards };
}
